import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { RefreshCw, ShieldAlert, Mail } from "lucide-react";
import { toast } from "sonner";
import { fetchPanelStatus, setApiPaused, triggerPanelSync } from "@/lib/panelApi";
import { QuotaCard, PanelList, fmtDT } from "@/components/panel/QuotaCard";

const LEVEL = { aviso: "text-violet-200", "crítico": "text-amber-200", bloqueo: "text-red-300" };

export default function MeteocatPanelPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await fetchPanelStatus(token));
    } catch (e) {
      if (e?.response?.status === 404) setDenied(true);
      else toast.error("No se pudo cargar el panel");
    }
  }, [token]);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    load();
    const t = setInterval(load, 30000);
    return () => { clearInterval(t); meta.remove(); };
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    await triggerPanelSync(token);
    toast("Sincronización lanzada · respeta la cuota");
    setTimeout(() => { load(); setSyncing(false); }, 6000);
  };

  const togglePause = async (q) => {
    await setApiPaused(token, q.id, !q.paused);
    load();
  };

  if (denied) return <div className="py-24 text-center text-white/50" data-testid="panel-denied">Página no encontrada</div>;
  if (!data) return <div className="py-24 text-center text-white/50" data-testid="panel-loading">Cargando panel…</div>;

  return (
    <div className="pb-16" data-testid="meteocat-panel">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-violet-300/70">Panel privado</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Consumo Meteocat</h1>
          <p className="text-xs text-white/50 mt-2">
            Periodo {data.period} · mes transcurrido {data.month_progress_pct}% · último CRON {fmtDT(data.last_cron_at)} · consumo real sincronizado {fmtDT(data.remote.synced_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[11px] flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${data.email_alerts ? "border-violet-400/40 text-violet-100" : "border-white/10 text-white/50"}`} data-testid="panel-email-status">
            <Mail size={12} /> {data.email_alerts ? "Avisos por email activos" : "Email de avisos sin configurar"}
          </span>
          <button onClick={sync} disabled={syncing} className="creep-btn-ghost inline-flex items-center gap-2 text-sm !py-1.5 !px-3" data-testid="panel-sync-btn">
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> Sincronizar ahora
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {data.quotas.map((q) => <QuotaCard key={q.id} q={q} onTogglePause={togglePause} />)}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PanelList
          title="Alertas de cuota" testId="panel-alerts" items={data.alerts} empty="Sin alertas este mes."
          render={(a, i) => (
            <li key={i} className="flex items-center justify-between text-xs border-b border-white/5 pb-2">
              <span className={`flex items-center gap-2 ${LEVEL[a.level]}`}><ShieldAlert size={13} /> {a.label} · {a.threshold}%</span>
              <span className="text-white/45">{a.count}/{a.limit} · {fmtDT(a.at)}{a.emailed ? " · email" : ""}</span>
            </li>
          )}
        />
        <PanelList
          title="Últimas actualizaciones de datos" testId="panel-caches" items={data.caches} empty="Sin datos en caché."
          render={(c) => (
            <li key={c.key} className="flex items-center justify-between text-xs border-b border-white/5 pb-2">
              <span className="text-white/80 font-mono">{c.key}</span>
              <span className="text-white/45">{fmtDT(c.fetched_at)} · hace {c.age_min < 60 ? `${c.age_min} min` : c.age_min < 2880 ? `${Math.round(c.age_min / 60)} h` : `${Math.round(c.age_min / 1440)} días`}</span>
            </li>
          )}
        />
        <PanelList
          title="Ejecuciones (CRON / manual)" testId="panel-runs" items={data.runs} empty="Aún no se ha ejecutado."
          render={(r, i) => (
            <li key={i} className="text-xs border-b border-white/5 pb-2">
              <div className="flex justify-between text-white/80"><span className="uppercase tracking-wider text-[10px]">{r.trigger}</span><span className="text-white/45">{fmtDT(r.started_at)}</span></div>
              {Object.entries(r.results || {}).map(([k, v]) => (
                <div key={k} className="text-white/55"><span className="text-violet-300/80">{k}</span> · {v}</div>
              ))}
            </li>
          )}
        />
        <PanelList
          title="Errores recientes" testId="panel-errors" items={data.errors} empty="Sin errores registrados."
          render={(e, i) => (
            <li key={i} className="text-xs border-b border-white/5 pb-2">
              <div className="flex justify-between"><span className="text-red-300/90">{e.api} · {e.status ?? "—"}</span><span className="text-white/45">{fmtDT(e.at)}</span></div>
              <div className="text-white/55 truncate" title={e.error}>{e.path} · {e.error}</div>
            </li>
          )}
        />
        <PanelList
          title="Últimas peticiones HTTP a Meteocat" testId="panel-calls" items={data.calls} empty="Sin peticiones."
          render={(c, i) => (
            <li key={i} className="flex items-center justify-between text-xs border-b border-white/5 pb-2 gap-3">
              <span className={`font-mono truncate ${c.ok ? "text-white/70" : "text-red-300"}`}>{c.status ?? "ERR"} {c.path}</span>
              <span className="text-white/45 shrink-0">{c.ms} ms · {fmtDT(c.at)}</span>
            </li>
          )}
        />
        <PanelList
          title="Planes según Meteocat" testId="panel-remote-plans" items={data.remote.plans || []} empty="Aún no sincronizado."
          render={(p) => (
            <li key={p.nom} className="flex items-center justify-between text-xs border-b border-white/5 pb-2">
              <span className="text-white/80">{p.nom}</span>
              <span className="text-white/55">{p.consultesRealitzades} / {p.maxConsultes} · quedan {p.consultesRestants}</span>
            </li>
          )}
        />
      </section>
    </div>
  );
}
